import { type WorkspaceAgentMessagePill } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';

export type FlattenedWorkspaceAgentAssetPillItem = {
  assetId: string;
  /** Clip id for `Clips.listByIds` when a version is known. */
  clipId?: string;
  versionId?: string;
};

/** Shared by outbound send normalization and display stack previews. */
export const getWorkspaceAgentAssetSubjectKey = (assetId: string, versionId?: string | null): string =>
  `${assetId}:${versionId ?? ''}`;

export const getFlattenedWorkspaceAgentAssetPillItemKey = (item: FlattenedWorkspaceAgentAssetPillItem): string =>
  getWorkspaceAgentAssetSubjectKey(item.assetId, item.versionId);

const expandWorkspaceAgentAssetPill = (pill: WorkspaceAgentMessagePill): FlattenedWorkspaceAgentAssetPillItem[] => {
  if (pill.type === 'asset') {
    return [
      {
        assetId: pill.assetId,
        clipId: pill.versionId,
        versionId: pill.versionId,
      },
    ];
  }

  if (pill.type === 'assetList') {
    return (pill.items ?? []).map((item) => ({
      assetId: item.assetId,
      clipId: item.versionId,
      versionId: item.versionId,
    }));
  }

  return [];
};

/**
 * Expands single-asset and `assetList` pills into ordered asset rows for previews and clip resolution.
 * Duplicate subjects (e.g. the same clip on both an `asset` pill and an `assetList` item) collapse to one
 * row so stack previews and uncited-stack fingerprints stay aligned.
 */
export const flattenWorkspaceAgentAssetPillItems = (
  pills: readonly WorkspaceAgentMessagePill[],
): FlattenedWorkspaceAgentAssetPillItem[] => {
  const seenSubjectKeys = new Set<string>();
  const items: FlattenedWorkspaceAgentAssetPillItem[] = [];

  for (const pill of pills) {
    for (const item of expandWorkspaceAgentAssetPill(pill)) {
      const subjectKey = getFlattenedWorkspaceAgentAssetPillItemKey(item);

      if (seenSubjectKeys.has(subjectKey)) {
        continue;
      }

      seenSubjectKeys.add(subjectKey);
      items.push(item);
    }
  }

  return items;
};

/** Clip id for a single asset pill when a version is known (`Clips.listByIds` keys on clip ids). */
export const getWorkspaceAgentAssetPillClipId = (pill: WorkspaceAgentMessagePill | undefined) =>
  pill?.type === 'asset' ? flattenWorkspaceAgentAssetPillItems([pill])[0]?.clipId : undefined;
